/* ──────────────────────────────────────────────────────────────────────
 *  Setup State Store — Team C owns this file
 *  Zustand store that persists to localStorage.
 *  Survives page refresh, auth redirects, and checkout returns.
 * ────────────────────────────────────────────────────────────────────── */

import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  AssistantTemplate,
  AssistantRules,
  CommercialAgentCatalogItem,
  ConnectionSelection,
  PlanId,
  ProvisioningStatus,
  SetupStep,
} from "@/types";

interface SetupState {
  // ─── Session Identity ──────────────────────────────────────────────
  sessionId: string;
  source: string | null;
  industry: string | null;

  // ─── Step 1: Assistant ─────────────────────────────────────────────
  selectedAgent: CommercialAgentCatalogItem | null;
  selectedTemplate: AssistantTemplate | null;

  // ─── Step 2: Connections ───────────────────────────────────────────
  connections: ConnectionSelection[];

  // ─── Step 3: Rules ─────────────────────────────────────────────────
  rules: AssistantRules | null;

  // ─── Step 4: Plan ──────────────────────────────────────────────────
  selectedPlan: PlanId;

  // ─── Checkout / Provisioning ───────────────────────────────────────
  stripeCheckoutSessionId: string | null;
  provisioningStatus: ProvisioningStatus | null;
  assistantId: string | null;

  // ─── Current Step ──────────────────────────────────────────────────
  currentStep: SetupStep;

  // ─── Actions ───────────────────────────────────────────────────────
  setAgent: (agent: CommercialAgentCatalogItem) => void;
  setTemplate: (template: AssistantTemplate) => void;
  setConnections: (connections: ConnectionSelection[]) => void;
  setRules: (rules: AssistantRules) => void;
  setPlan: (plan: PlanId) => void;
  setCurrentStep: (step: SetupStep) => void;
  setSource: (source: string) => void;
  setIndustry: (industry: string) => void;
  clearIndustry: () => void;
  setStripeSession: (sessionId: string) => void;
  setProvisioningStatus: (status: ProvisioningStatus) => void;
  setAssistantId: (id: string) => void;
  resetSetup: () => void;
}

function generateSessionId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `setup-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const INITIAL_STATE = {
  sessionId: generateSessionId(),
  source: null,
  industry: null,
  selectedAgent: null,
  selectedTemplate: null,
  connections: [],
  rules: null,
  selectedPlan: "starter" as PlanId,
  stripeCheckoutSessionId: null,
  provisioningStatus: null,
  assistantId: null,
  currentStep: "package" as SetupStep,
};

const PLAN_ORDER: Record<PlanId, number> = { starter: 1, team: 2, business: 3 };

export function agentToTemplate(agent: CommercialAgentCatalogItem): AssistantTemplate {
  return {
    id: agent.catalogId,
    name: agent.name,
    description: agent.summary,
    bestFor: agent.bestFor,
    defaultConnections: agent.defaultConnectionIds,
    icon: "Bot",
    status: agent.setupStatus === "custom_setup" ? "custom_setup" : "available",
    sources: agent.defaultConnectionIds.join(", ") || "Selected business sources",
    prepares: agent.summary,
    humanReview: "External sends, source expansion, sensitive data, and business decisions",
  };
}

export const useSetupStore = create<SetupState>()(
  persist(
    (set) => ({
      ...INITIAL_STATE,

      setAgent: (agent) =>
        set({ selectedAgent: agent, selectedTemplate: agentToTemplate(agent), currentStep: "connections" }),

      setTemplate: (template) =>
        set({ selectedTemplate: template, currentStep: "connections" }),

      setConnections: (connections) =>
        set({ connections, currentStep: "rules" }),

      setRules: (rules) => set({ rules, currentStep: "review" }),

      setPlan: (plan) => set({ selectedPlan: plan }),

      setCurrentStep: (step) => set({ currentStep: step }),

      setSource: (source) => set({ source }),

      setIndustry: (industry) => set({ industry }),

      clearIndustry: () => set({ industry: null }),

      setStripeSession: (sessionId) =>
        set({ stripeCheckoutSessionId: sessionId }),

      setProvisioningStatus: (status) =>
        set({ provisioningStatus: status }),

      setAssistantId: (id) => set({ assistantId: id }),

      resetSetup: () => set({ ...INITIAL_STATE, sessionId: generateSessionId() }),
    }),
    {
      name: "pulse-connectors-setup",
      version: 1,
    },
  ),
);

// ─── Validation Helpers ──────────────────────────────────────────────

export function validateRules(rules: AssistantRules | null): string[] {
  const errors: string[] = [];
  if (!rules) return ["Assistant rules are required"];
  if (!rules.name.trim()) errors.push("Assistant name is required");
  if (!rules.mainJob.trim()) errors.push("Main job description is required");
  if (!rules.reviewOwner.trim()) errors.push("Review owner is required");
  if (rules.allowedSources.length === 0)
    errors.push("At least one allowed source is required");
  if (!rules.sensitiveDataAcknowledged)
    errors.push("Sensitive data acknowledgment is required");
  if (!rules.outputDestination.trim())
    errors.push("Output destination is required");
  return errors;
}

export function canProceedToCheckout(state: SetupState): boolean {
  const agentEligible = !state.selectedAgent || PLAN_ORDER[state.selectedPlan] >= PLAN_ORDER[state.selectedAgent.minimumPlan];
  return (
    (state.selectedAgent !== null || state.selectedTemplate !== null) &&
    agentEligible &&
    state.connections.some((c) => c.selected) &&
    state.rules !== null &&
    validateRules(state.rules).length === 0
  );
}
